import React, { useMemo, useRef, useEffect } from "react";
import ProductImage from "../Shop/ProductImage";
import { useProfil } from "../../contexts/ProfilContext";
import { formatCHF } from "../../utils/formatCHF";

const HomeActionBanner = ({ produits, onActionClick, setVueActuelle, t }) => {
  const { authStatus } = useProfil();
  const isVisitor = authStatus === "visitor";

  const promoProducts = useMemo(() => {
    if (!Array.isArray(produits) || produits.length === 0) return [];

    /**
     * ==========================================================
     * GUIDE — HELPERS BOOLÉENS TOLÉRANTS
     * ==========================================================
     *
     * On garde une lecture souple des flags backend / legacy.
     */
    const isTruthyFlag = (value) =>
      value === true ||
      value === 1 ||
      value === "1" ||
      value === "true" ||
      value === "oui" ||
      value === "yes";

    /**
     * ==========================================================
     * GUIDE — FLAGS BANNIÈRE PRODUITS CAVE
     * ==========================================================
     *
     * Logique legacy cave conservée telle quelle.
     */
    const isCaveBannerProduct = (p) => {
      if (!p) return false;

      return (
        isTruthyFlag(p?.addToBanner) ||
        isTruthyFlag(p?.isBanner) ||
        isTruthyFlag(p?.banner) ||
        isTruthyFlag(p?.homeBanner) ||
        isTruthyFlag(p?.isHomeBanner) ||
        isTruthyFlag(p?.bannerHome) ||
        isTruthyFlag(p?.caveBanner) ||
        isTruthyFlag(p?.isCaveBanner) ||
        isTruthyFlag(p?.addToCaveBanner) ||
        isTruthyFlag(p?.showInBanner) ||
        isTruthyFlag(p?.showOnBanner) ||
        isTruthyFlag(p?.afficherBanniere) ||
        isTruthyFlag(p?.afficher_banner) ||
        isTruthyFlag(p?.__forceBanner)
      );
    };

    /**
     * ==========================================================
     * GUIDE — NORMALISATION GROUPE / NOM
     * ==========================================================
     *
     * Le banner_group sert à éviter les doublons visuels.
     * Si absent, on retombe sur un nom normalisé.
     */
    const normalizeText = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

    /**
     * ==========================================================
     * GUIDE — CALCUL D’UN POURCENTAGE DE RABAIS
     * ==========================================================
     *
     * Sert au tri secondaire si plusieurs produits sont éligibles.
     * On reste tolérant sur les formats legacy.
     */
    const getDiscountPercent = (p) => {
      const audit = p?.audit || {};

      const currentRaw =
        p?.displayPrice ??
        audit?.displayPrice ??
        p?.finalPrice ??
        p?.price ??
        null;

      const baseRaw =
        p?.old_price ??
        p?.oldPrice ??
        audit?.oldPrice ??
        audit?.basePrice ??
        p?.basePrice ??
        null;

      const currentVal = Number(currentRaw);
      const baseVal = Number(baseRaw);

      if (!Number.isFinite(currentVal) || !Number.isFinite(baseVal)) return 0;
      if (currentVal <= 0 || baseVal <= 0) return 0;
      if (currentVal >= baseVal) return 0;

      const pct = Math.round(((baseVal - currentVal) / baseVal) * 100);
      return Number.isFinite(pct) && pct > 0 ? pct : 0;
    };

    /**
     * ==========================================================
     * GUIDE — CLÉ DE DÉDUPLICATION
     * ==========================================================
     *
     * Priorité :
     * 1. banner_group si renseigné
     * 2. nom normalisé
     * 3. id en dernier recours
     */
    const getBannerDedupKey = (p) => {
      const groupKey = normalizeText(p?.banner_group);
      if (groupKey) return `group:${groupKey}`;

      const nameKey = normalizeText(p?.name || p?.title || p?.titre);
      if (nameKey) return `name:${nameKey}`;

      return `id:${String(p?.id || "")}`;
    };

    /**
     * ==========================================================
     * GUIDE — TRI BANNIÈRE
     * ==========================================================
     *
     * Ordre retenu :
     * 1. banner_rank ASC
     * 2. plus fort rabais d’abord
     * 3. nom alphabétique
     * 4. id
     *
     * Cela rend la bannière stable et pilotable.
     */
    const compareBannerProducts = (a, b) => {
      const rankA = Number.isFinite(Number(a?.banner_rank))
        ? Number(a.banner_rank)
        : 9999;
      const rankB = Number.isFinite(Number(b?.banner_rank))
        ? Number(b.banner_rank)
        : 9999;

      if (rankA !== rankB) return rankA - rankB;

      const discountA = getDiscountPercent(a);
      const discountB = getDiscountPercent(b);
      if (discountA !== discountB) return discountB - discountA;

      const nameA = String(a?.name || "").localeCompare(
        String(b?.name || ""),
        "fr",
        {
          sensitivity: "base",
        }
      );
      if (nameA !== 0) return nameA;

      return Number(a?.id || 0) - Number(b?.id || 0);
    };

    /**
     * ==========================================================
     * GUIDE — ÉLIGIBILITÉ BANNIÈRE
     * ==========================================================
     *
     * PRODUITS CAVE :
     * - logique legacy par flag bannière conservée
     *
     * PRODUITS NORMAUX :
     * - show_in_home_banner = 1
     * - isDiscount = 1
     *
     * IMPORTANT :
     * - isOnline ne bloque pas
     * - stock ne bloque pas
     * - on veut une logique éditoriale, pas purement catalogue
     */
    const eligible = produits.filter((p) => {
      if (!p || !p.id) return false;

      const hasPrice =
        p.displayPrice !== undefined ||
        p.price !== undefined ||
        p.audit?.displayPrice !== undefined;

      if (!hasPrice) return false;

      const isCaveProduct = p?.__isCave === true;

      if (isCaveProduct) {
        return isCaveBannerProduct(p);
      }

      const isDiscount =
        Number(p?.isDiscount ?? p?.is_discount ?? 0) === 1 ||
        p?.isDiscount === true;

      const showInBanner =
        Number(p?.show_in_home_banner ?? 0) === 1 ||
        p?.show_in_home_banner === true;

      return showInBanner && isDiscount;
    });

    /**
     * ==========================================================
     * GUIDE — TRI AVANT DÉDUPLICATION
     * ==========================================================
     *
     * On trie d’abord pour que le "meilleur" candidat d’un groupe
     * soit conservé lors de la déduplication.
     */
    const sorted = [...eligible].sort(compareBannerProducts);

    /**
     * ==========================================================
     * GUIDE — DÉDUPLICATION
     * ==========================================================
     *
     * Un seul produit par banner_group (ou nom normalisé si pas de groupe).
     */
    const seen = new Set();
    const deduped = [];

    for (const product of sorted) {
      const key = getBannerDedupKey(product);
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(product);
    }

    /**
     * ==========================================================
     * GUIDE — LIMITE VISUELLE
     * ==========================================================
     *
     * On garde une bannière maîtrisée.
     * Ajustable plus tard si besoin.
     */
    return deduped.slice(0, 12);
  }, [produits]);

  const scrollerRef = useRef(null);
  const draggedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const lastInteractionAtRef = useRef(Date.now());

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (promoProducts.length === 0) return;

    const AUTO_SPEED = 0.45;
    const IDLE_DELAY_MS = 1200;

    let rafId = 0;
    let mounted = true;

    const maxScrollLeft = () => Math.max(0, el.scrollWidth - el.clientWidth);

    const step = () => {
      if (!mounted) return;

      const idle = Date.now() - lastInteractionAtRef.current > IDLE_DELAY_MS;

      if (!isDraggingRef.current && idle) {
        const max = maxScrollLeft();
        if (max > 0) {
          el.scrollLeft += AUTO_SPEED;
          if (el.scrollLeft >= max) {
            el.scrollLeft = 0;
          }
        }
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);

    return () => {
      mounted = false;
      cancelAnimationFrame(rafId);
    };
  }, [promoProducts.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const DRAG_THRESHOLD = 8;

    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;

      const dx = e.clientX - startXRef.current;
      if (Math.abs(dx) > DRAG_THRESHOLD) {
        draggedRef.current = true;
      }

      el.scrollLeft = startScrollLeftRef.current - dx;
    };

    const endMouseDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      el.classList.remove("dragging");
      lastInteractionAtRef.current = Date.now();

      window.setTimeout(() => {
        draggedRef.current = false;
      }, 80);
    };

    const onMouseDown = (e) => {
      if (e.button !== 0) return;

      isDraggingRef.current = true;
      draggedRef.current = false;
      startXRef.current = e.clientX;
      startScrollLeftRef.current = el.scrollLeft;
      lastInteractionAtRef.current = Date.now();
      el.classList.add("dragging");
    };

    const onTouchStart = (e) => {
      if (!e.touches || e.touches.length !== 1) return;

      const touch = e.touches[0];
      isDraggingRef.current = true;
      draggedRef.current = false;
      startXRef.current = touch.clientX;
      startScrollLeftRef.current = el.scrollLeft;
      lastInteractionAtRef.current = Date.now();
      el.classList.add("dragging");
    };

    const onTouchMove = (e) => {
      if (!isDraggingRef.current || !e.touches || e.touches.length !== 1)
        return;

      const touch = e.touches[0];
      const dx = touch.clientX - startXRef.current;

      if (Math.abs(dx) > DRAG_THRESHOLD) {
        draggedRef.current = true;
      }

      el.scrollLeft = startScrollLeftRef.current - dx;

      if (draggedRef.current) {
        e.preventDefault();
      }
    };

    const endTouchDrag = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      el.classList.remove("dragging");
      lastInteractionAtRef.current = Date.now();

      window.setTimeout(() => {
        draggedRef.current = false;
      }, 80);
    };

    const onWheel = () => {
      lastInteractionAtRef.current = Date.now();
    };

    const onMouseEnter = () => {
      lastInteractionAtRef.current = Date.now();
    };

    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", endMouseDrag);

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", endTouchDrag, { passive: true });
    el.addEventListener("touchcancel", endTouchDrag, { passive: true });

    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("mouseenter", onMouseEnter);

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endMouseDrag);

      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", endTouchDrag);
      el.removeEventListener("touchcancel", endTouchDrag);

      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("mouseenter", onMouseEnter);
    };
  }, []);

  if (authStatus === "loading" || promoProducts.length === 0) return null;

  const handleViewAll = () => setVueActuelle?.("actions");

  return (
    <div className="home-action-banner-overlay">
      <div className="banner-header-light">
        <button
          type="button"
          className="banner-title-clickable"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleViewAll();
          }}
        >
          {t?.exclusiveOffers || "PROFITEZ DE NOS OFFRES EXCLUSIVES"}
        </button>
      </div>
      <div className="horizontal-scroll-container" ref={scrollerRef}>
        {promoProducts.map((p) => {
          if (!p?.id) return null;

          const finalPrice =
            p.displayPrice ?? p.audit?.displayPrice ?? p.price ?? 0;

          return (
            <div
              key={p.id}
              className="scroll-card-mini"
              onClick={() => {
                if (draggedRef.current) return;
                onActionClick?.(p);
              }}
            >
              <div className="scroll-image-wrapper">
                <ProductImage product={p} className="scroll-img" />

                {p?.__isCave ? (
                  <div className="scroll-badge-mini badge-cave">
                    {t?.wineUniverse || "CAVE"}
                  </div>
                ) : p.audit?.discountLabel ? (
                  <div className="scroll-badge-mini">
                    {p.audit.discountLabel}
                  </div>
                ) : null}
              </div>

              <div className="scroll-details-mini">
                <span className="scroll-name-mini">{p.name}</span>
              </div>

              <div className="scroll-price-container">
                {isVisitor ? (
                  <div className="price-visitor-wrapper">
                    <span className="scroll-price-mini visitor-blur">
                      {formatCHF(finalPrice)}
                    </span>
                    <span className="lock-badge">
                      🔒 {t?.memberPriceShort || "MEMBRES"}
                    </span>
                  </div>
                ) : (
                  <span className="scroll-price-mini">
                    {formatCHF(finalPrice)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <style jsx="true">{`
        .home-action-banner-overlay {
          margin: 0 auto 30px;
          width: 95%;
          max-width: 1000px;
          background: transparent;
          padding: 18px 0 8px;
          border: none;
          border-radius: 0;
          overflow: hidden;
          box-shadow: none;
        }

        .banner-header-light {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 0 20px 18px 20px;
        }

        .banner-title-clickable {
          font-family: "Inter", sans-serif;
          letter-spacing: 1.5px;
          font-weight: 800;
          font-size: 0.85rem;
          color: #b08a45;
          text-transform: uppercase;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: transform 0.2s ease, opacity 0.2s ease;
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.65);
        }

        .banner-title-clickable:hover {
          transform: translateY(-1px);
          opacity: 0.85;
        }

        .horizontal-scroll-container {
          display: flex;
          gap: 15px;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 5px 20px 12px;
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
          cursor: grab;
          user-select: none;
          touch-action: pan-x;
        }

        .horizontal-scroll-container.dragging {
          cursor: grabbing;
        }

        .horizontal-scroll-container::-webkit-scrollbar {
          display: none;
        }

        .scroll-card-mini {
  flex: 0 0 140px;
  min-width: 140px;
  box-sizing: border-box;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: transparent;
  backdrop-filter: none;
  border: 1px solid rgba(176, 138, 69, 0.08);
  border-radius: 16px;
  padding-bottom: 10px;
  box-shadow: 0 8px 18px rgba(84, 66, 38, 0.035);
  overflow: hidden;
    backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

        .scroll-card-mini:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 22px rgba(84, 66, 38, 0.055);
}

        .scroll-image-wrapper {
  width: calc(100% - 18px);
  height: 100px;
  position: relative;
  margin: 9px auto 0;
  border-radius: 14px;
  overflow: hidden;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scroll-image-wrapper :global(img) {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  object-fit: cover !important;
  object-position: center center !important;
  display: block !important;
}

        .scroll-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  display: block;
  background: transparent;
  pointer-events: none;
}

        .scroll-badge-mini {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(176, 138, 69, 0.94);
          color: #fff;
          font-size: 0.65rem;
          padding: 3px 6px;
          font-weight: 800;
          border-radius: 999px;
          box-shadow: 0 5px 12px rgba(84, 66, 38, 0.16);
        }

        .badge-cave {
          background: rgba(111, 29, 27, 0.94);
        }

        .scroll-details-mini {
          margin-top: 10px;
          padding: 0 8px;
          display: flex;
          flex-direction: column;
        }

        .scroll-name-mini {
          font-size: 0.8rem;
          font-weight: 600;
          color: rgba(28, 24, 19, 0.88);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .scroll-price-container {
          margin-top: 4px;
          height: 38px;
          padding: 0 8px;
        }

        .scroll-price-mini {
          font-size: 0.85rem;
          font-weight: 800;
          color: #8f6d33;
        }

        .price-visitor-wrapper {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .visitor-blur {
          color: rgba(80, 72, 60, 0.5);
          filter: blur(0.4px);
          font-size: 0.8rem;
        }

        .lock-badge {
          font-size: 0.65rem;
          color: #b08a45;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        @media (max-width: 780px) {
          .home-action-banner-overlay {
            width: 100%;
            margin-bottom: 26px;
            padding: 16px 0 6px;
          }

          .banner-header-light {
            padding: 0 16px 16px 16px;
          }

          .banner-title-clickable {
            letter-spacing: 1px;
            font-size: 0.75rem;
            color: #b08a45;
          }

          .horizontal-scroll-container {
            padding: 5px 16px 12px;
          }
        }
      `}</style>{" "}
    </div>
  );
};

export default HomeActionBanner;
